import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { Chip, Flex, Icon, Span } from "@goodfill/ui-components";
import { PricedBond } from "../PricedBond";
import { SortAndFilterTools } from "./SortAndFilterTools";
import { BondFilterCriteria } from "./filter/BondFilterCriteria";
import { calculateDataConstraints } from "./filter/BondFilterDataConstraints";
import { createDefaultValuesFromConstraints } from "./filter/createDefaultValuesFromConstraints";
import { BondChartView } from "./chart/BondChartView";
import { BondFilterChange, BondFilterChipData, BondFilterData } from "./filter/BondFilter";
import { SortKey } from "./sort/SortingDropdown";
import { BondSortDirection, sortBondList } from "./sort/sortBondList";
import { InvestmentAssetsFeed } from "./InvestmentAssetsFeed";
import { bondFilterFavorite, bondFilters } from "./filter/bondFilters";
import { BondListViewId } from "../card/BondCardViewSelector";
import "./styles/ListBondsTable.styles.scss";
import { filterPopupDockedState, pricedBondState, userFavoriteBondsState } from "../../../state";
import { VirtualizedBondsList } from "./VirtualizedBondsList";
import { CorporateBondId } from "@goodfill/domain-properties";
import { BatchBuyButtons, BatchBuyModal, SelectedToBatchBuyContext } from "../batchBuy/SelectedToBatchBuyContext";

function loadStoredFilters(): BondFilterCriteria {
  let criteria: Partial<BondFilterCriteria> = {};
  for (const bondFilter of bondFilters) {
    criteria = bondFilter.applyCriteriaFromStorage(criteria);
  }

  return criteria as BondFilterCriteria;
}

function saveStoredFilters(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria) {
  for (const bondFilter of bondFilters) {
    bondFilter.storeCriteriaToStorage(criteria, defaultCriteria);
  }
}

function getAllChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
  let chips: BondFilterChipData[] = [];
  for (const bondFilter of bondFilters) {
    chips = [...chips, ...bondFilter.getChips(criteria, defaultCriteria)];
  }

  return chips;
}

function mergeCriteria(a: BondFilterCriteria, b: Partial<BondFilterCriteria>): BondFilterCriteria {
  return {
    ...a,
    ...Object.fromEntries(
      // Remove undefined stored filters.
      Object.entries(b).filter(([, value]) => typeof value !== "undefined"),
    ),
  };
}

export const ListBondsTable = () => {
  const data = useRecoilValue(pricedBondState);
  const userFavoriteBonds = useRecoilValue(userFavoriteBondsState);
  const { t } = useTranslation();
  const filterIsDocked = useRecoilValue(filterPopupDockedState);

  const [selectedBondsToBatchBuy, setSelectedBondsToBatchBuy] = useState<CorporateBondId[]>([]);
  const [isBatchBuyDialogOpen, setIsBatchBuyDialogOpen] = useState(false);

  // Important useMemo to prevent useEffect to setCriteria from triggering
  const bondFilerConstraints = useMemo(() => calculateDataConstraints(Array.from(data.values()), bondFilters), [data]);
  // Important useMemo to prevent useEffect to setCriteria from triggering
  const defaultCriteria = useMemo(
    () => createDefaultValuesFromConstraints(bondFilerConstraints),
    [bondFilerConstraints],
  );
  // Important useMemo to prevent useEffect to setCriteria from triggering
  const storedCriteria = useMemo(() => loadStoredFilters(), []);

  // Selected bondListViewId persists in localStorage, so when user
  // goes back to bonds view he/she sees the same selected bond view.
  const [bondListViewId, setBondListViewId] = useState<BondListViewId>(
    (globalThis?.window?.localStorage.getItem("bondListViewId") as BondListViewId) ?? "compact",
  );

  const [criteria, setCriteria] = useState<BondFilterCriteria>(mergeCriteria(defaultCriteria, storedCriteria));

  const activeFilterChips = getAllChips(criteria, defaultCriteria);

  const onFilterChange = (change: BondFilterChange) => {
    setCriteria(change.criteria);
  };

  useEffect(() => {
    saveStoredFilters(criteria, defaultCriteria);
  }, [criteria, defaultCriteria]);

  // Selected sortKey and sortDirection persist in localStorage,
  // so when user goes back to bonds view he/she sees the same sorting.
  const [selectedSortKey, setSelectedSortKey] = useState<SortKey>(
    (globalThis?.window?.localStorage.getItem("sortKey") as SortKey) ?? "issuer",
  );
  const [selectedSortDirection, setSelectedSortDirection] = useState<BondSortDirection>(
    (globalThis?.window?.localStorage.getItem("sortDirection") as BondSortDirection) ?? "ASC",
  );

  const sortedFilteredRows: PricedBond[] = (() => {
    const pricedBondsArray = Array.from(data.values());
    const filteredRows = pricedBondsArray.filter((r) => {
      for (const bondFilter of bondFilters) {
        const bondFilterData: BondFilterData = {
          pricedBond: r,
          isFavorite: userFavoriteBonds.includes(r.bondIdCusip),
        };
        if (!bondFilter.evaluate(bondFilterData, criteria)) {
          return false;
        }
      }

      return true;
    });

    return sortBondList(filteredRows, selectedSortKey, selectedSortDirection);
  })();

  useEffect(() => {
    globalThis?.window?.localStorage.setItem("sortKey", selectedSortKey);
    globalThis?.window?.localStorage.setItem("sortDirection", selectedSortDirection);
  }, [selectedSortKey, selectedSortDirection]);

  useEffect(() => {
    globalThis?.window?.localStorage.setItem("bondListViewId", bondListViewId);
  }, [bondListViewId]);

  const totalItems = Array.from(data.values()).length;

  const onRowRemove = (rowId: CorporateBondId) => {
    setSelectedBondsToBatchBuy((selectedIds) => selectedIds.filter((selectedId) => selectedId !== rowId));
  };

  return (
    <SelectedToBatchBuyContext.Provider
      value={{
        selected: selectedBondsToBatchBuy,
        setSelected: setSelectedBondsToBatchBuy,
        isDialogOpen: isBatchBuyDialogOpen,
        setIsDialogOpen: setIsBatchBuyDialogOpen,
        onRowRemove,
      }}
    >
      <div className="list-bond-table">
        <InvestmentAssetsFeed />

        <div className="dashboard-main">
          <Flex direction="col">
            <Flex direction="col" verticalMargin={4} gap={8} className="md:my-2">
              <Flex direction="rowCol" justify="start" gap={12}>
                <SortAndFilterTools
                  bondFilters={bondFilters}
                  constraints={bondFilerConstraints}
                  criteria={criteria}
                  defaultCriteria={defaultCriteria}
                  favoriteBonFilter={bondFilterFavorite}
                  numItems={sortedFilteredRows.length}
                  onFilterChange={onFilterChange}
                  selectedSortDirection={selectedSortDirection}
                  selectedSortKey={selectedSortKey}
                  selectedViewId={bondListViewId}
                  setSelectedSortDirection={setSelectedSortDirection}
                  setSelectedSortKey={setSelectedSortKey}
                  setSelectedViewId={setBondListViewId}
                  totalItems={totalItems}
                />
              </Flex>

              <div className="flex flex-row justify-end gap-1 flex-wrap">
                {activeFilterChips.map(({ id, label, onClick }) => (
                  <Chip key={id} className="pl-0 py-0">
                    <>
                      {label}
                      &nbsp;
                      <Icon name="x" size="0.8em" onClick={() => setCriteria(onClick())} />
                    </>
                  </Chip>
                ))}
              </div>

              {bondListViewId === "wide" ? (
                <div className="flex flex-row justify-end gap-1 flex-wrap">
                  <BatchBuyButtons rows={sortedFilteredRows} />
                </div>
              ) : null}
            </Flex>
            {bondListViewId === "charts" ? (
              <BondChartView
                pricedBonds={sortedFilteredRows}
                isFavoriteSelected={activeFilterChips.find((a) => a.id === "favorite") !== undefined}
              />
            ) : (
              <div className={`${bondListViewId}${filterIsDocked ? "-isdocked" : ""}`}>
                {sortedFilteredRows.length === 0 && (
                  <Flex wrap={true} gap={7} justify="center">
                    <Flex grow={1} verticalMargin={12} verticalPadding={12} horizontalPadding={4}>
                      <Span size="xl">{t("search.noBoundFound")}</Span>
                    </Flex>
                  </Flex>
                )}

                <BatchBuyModal rows={sortedFilteredRows} />
                <VirtualizedBondsList items={sortedFilteredRows} wide={bondListViewId === "wide"} />
              </div>
            )}
          </Flex>
        </div>
      </div>
    </SelectedToBatchBuyContext.Provider>
  );
};
